import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/Button';
import { Toast } from '../components/Toast';
import { AlertCircleIcon, EyeIcon, EyeOffIcon, HandshakeIcon, MailIcon, LockIcon, PackageIcon, CalendarIcon, CheckIcon } from 'lucide-react';
type UserRole = 'brand' | 'organizer';
export function Register() {
  const navigate = useNavigate();
  const {
    register,
    currentUser
  } = useAuth();
  const [userType, setUserType] = useState<UserRole>('brand');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    type: 'success' as 'success' | 'error' | 'info' | 'warning',
    message: ''
  });
  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate(currentUser.type === 'brand' ? '/dashboard/brand' : '/dashboard/organizer');
    }
  }, [currentUser, navigate]);
  // Password strength calculation
  const getPasswordStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };
  const passwordStrength = getPasswordStrength();
  const strengthLabels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Passwords do not match'
      });
      return;
    }
    if (password.length < 6) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Password must be at least 6 characters'
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await register(email, password, userType, '');
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Account created! Redirecting to setup...'
      });
      setTimeout(() => {
        navigate(userType === 'brand' ? '/onboarding/brand' : '/onboarding/organizer');
      }, 1500);
    } catch (error: any) {
      let errorMessage = 'Failed to create account';
      // Handle email confirmation required
      if (error.message?.includes('EMAIL_CONFIRMATION_REQUIRED')) {
        setToast({
          isVisible: true,
          type: 'info',
          message: 'Check your email! We sent you a confirmation link to verify your account.'
        });
        return;
      }
      if (error.message?.includes('already') || error.message?.includes('already registered')) {
        errorMessage = 'An account with this email already exists';
      } else if (error.message?.includes('Database not configured') || error.message?.includes('not configured')) {
        errorMessage = 'Backend not configured. Please set up Supabase credentials in your environment variables.';
      } else if (error.message?.includes('Invalid')) {
        errorMessage = 'Invalid email or password format';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setToast({
        isVisible: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col" data-id="element-3286">
      {/* Header */}
      <div className="p-6" data-id="element-3287">
        <Link to="/" className="inline-flex items-center space-x-2 group" data-id="element-3288">
          <div className="bg-blue-600 p-2 rounded-lg shadow-sm" data-id="element-3289">
            <HandshakeIcon className="h-5 w-5 text-white" data-id="element-3290" />
          </div>
          <span className="text-xl font-bold text-gray-900" data-id="element-3291">SponsrAI</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16" data-id="element-3292">
        <div className="w-full max-w-lg" data-id="element-3293">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8" data-id="element-3294">
            <div className="text-center mb-8" data-id="element-3295">
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3296">
                Create your account
              </h1>
              <p className="text-gray-500" data-id="element-3297">
                Join SponsrAI and start connecting
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3298">
              {/* Role Selection */}
              <div data-id="element-3299">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3300">
                  I am a...
                </label>
                <div className="grid grid-cols-2 gap-4" data-id="element-3301">
                  <button type="button" onClick={() => setUserType('brand')} className={`relative p-4 rounded-xl border-2 text-left transition-all ${userType === 'brand' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3302">
                    {userType === 'brand' && <div className="absolute top-3 right-3 bg-blue-500 rounded-full p-0.5" data-id="element-3303">
                        <CheckIcon className="h-3 w-3 text-white" data-id="element-3304" />
                      </div>}
                    <div className={`inline-flex p-2 rounded-lg mb-2 ${userType === 'brand' ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-3305">
                      <PackageIcon className={`h-5 w-5 ${userType === 'brand' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-3306" />
                    </div>
                    <div className="font-semibold text-gray-900" data-id="element-3307">Brand</div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-3308">
                      Find event partnerships
                    </div>
                  </button>

                  <button type="button" onClick={() => setUserType('organizer')} className={`relative p-4 rounded-xl border-2 text-left transition-all ${userType === 'organizer' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3309">
                    {userType === 'organizer' && <div className="absolute top-3 right-3 bg-purple-500 rounded-full p-0.5" data-id="element-3310">
                        <CheckIcon className="h-3 w-3 text-white" data-id="element-3311" />
                      </div>}
                    <div className={`inline-flex p-2 rounded-lg mb-2 ${userType === 'organizer' ? 'bg-purple-100' : 'bg-gray-100'}`} data-id="element-3312">
                      <CalendarIcon className={`h-5 w-5 ${userType === 'organizer' ? 'text-purple-600' : 'text-gray-500'}`} data-id="element-3313" />
                    </div>
                    <div className="font-semibold text-gray-900" data-id="element-3314">Organizer</div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-3315">
                      Find brand sponsors
                    </div>
                  </button>
                </div>
              </div>

              {/* Email */}
              <div data-id="element-3316">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3317">
                  Email address
                </label>
                <div className="relative" data-id="element-3318">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-3319">
                    <MailIcon className="h-5 w-5 text-gray-400" data-id="element-3320" />
                  </div>
                  <input id="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" placeholder="you@example.com" data-id="element-3321" />
                </div>
              </div>

              {/* Password */}
              <div data-id="element-3322">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3323">
                  Password
                </label>
                <div className="relative" data-id="element-3324">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-3325">
                    <LockIcon className="h-5 w-5 text-gray-400" data-id="element-3326" />
                  </div>
                  <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-11 pr-12 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" placeholder="Create a password" data-id="element-3327" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600" data-id="element-3328">
                    {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-3329" /> : <EyeIcon className="h-5 w-5" data-id="element-3330" />}
                  </button>
                </div>

                {/* Password Strength */}
                {password && <div className="mt-2" data-id="element-3331">
                    <div className="flex gap-1 mb-1" data-id="element-3332">
                      {[...Array(5)].map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'}`} data-id="element-3333" />)}
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-3334">
                      Password strength:{' '}
                      {strengthLabels[Math.max(0, passwordStrength - 1)] || 'Very weak'}
                    </p>
                  </div>}
              </div>

              {/* Confirm Password */}
              <div data-id="element-3335">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3336">
                  Confirm password
                </label>
                <div className="relative" data-id="element-3337">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-3338">
                    <LockIcon className="h-5 w-5 text-gray-400" data-id="element-3339" />
                  </div>
                  <input id="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`block w-full pl-11 pr-4 py-3 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow ${confirmPassword && password !== confirmPassword ? 'border-red-300 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'}`} placeholder="Confirm your password" data-id="element-3340" />
                </div>
                {confirmPassword && password !== confirmPassword && <p className="mt-1.5 text-sm text-red-600 flex items-center" data-id="element-3341">
                    <AlertCircleIcon className="h-4 w-4 mr-1" data-id="element-3342" />
                    Passwords do not match
                  </p>}
              </div>

              {/* Submit */}
              <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isSubmitting} disabled={isSubmitting || confirmPassword !== '' && password !== confirmPassword} data-id="element-3343">
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-8" data-id="element-3344">
              <div className="absolute inset-0 flex items-center" data-id="element-3345">
                <div className="w-full border-t border-gray-200" data-id="element-3346" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-3347">
                <span className="px-4 bg-white text-gray-500" data-id="element-3348">
                  Already have an account?
                </span>
              </div>
            </div>

            {/* Login Link */}
            <Link to="/login" className="block w-full text-center px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors" data-id="element-3349">
              Sign in instead
            </Link>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-8" data-id="element-3350">
            By creating an account, you agree to our{' '}
            <a href="/terms" className="text-blue-600 hover:text-blue-700" data-id="element-3351">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-600 hover:text-blue-700" data-id="element-3352">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>

      <Toast type={toast.type} message={toast.message} duration={5000} onClose={() => setToast({
      ...toast,
      isVisible: false
    })} isVisible={toast.isVisible} data-id="element-3353" />
    </div>;
}